import type { ApiResponse } from '@/types/auth';

// Validation failures (ErrorResponseFactory.ValidationError) put user-facing sentences like "Email is required" in `errors`
const VALIDATION_FAILED_MESSAGE = 'Validation failed';

// Labels GlobalExceptionHandlingMiddleware puts on ArgumentException / InvalidOperationException responses. Their
// `errors` carry raw exception text ("No transaction in progress", "Failed to upload file: <inner exception>"),
// which is for logs, not users - so neither the label nor the details are shown.
const INTERNAL_ERROR_LABELS = new Set([
  'Invalid argument',
  'Invalid operation',
  'Required parameter missing',
]);

/**
 * Picks the user-facing reason out of a failed ApiResponse.
 * Prefers the server's message; for validation failures uses the detailed errors instead; falls back to the
 * caller's text when the server has nothing a user can act on.
 */
export function getApiErrorMessage(
  response: Partial<Pick<ApiResponse<unknown>, 'success' | 'message' | 'errors'>>,
  fallback: string
): string {
  // A successful envelope that still failed a caller's check (e.g. missing data) carries a success message, not a reason
  if (response.success) return fallback;

  const message = response.message?.trim();
  if (message && INTERNAL_ERROR_LABELS.has(message)) return fallback;

  if (message === VALIDATION_FAILED_MESSAGE) {
    const details = (response.errors ?? []).filter((e) => typeof e === 'string' && e.trim());
    // Validator messages have no trailing period ("Email is required"), so add one before joining several
    if (details.length > 0) return details.map((e) => (/[.!?]$/.test(e.trim()) ? e.trim() : `${e.trim()}.`)).join(' ');
  }

  return message || fallback;
}

/**
 * A failed API call that keeps its HTTP status, so the query retry rule can tell an answer that won't change
 * (not found, deleted, forbidden) from a transient failure.
 */
export class ApiRequestError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
  }
}

/**
 * Message to show for an error caught from a mutation or API call.
 * Hooks throw `new Error(getApiErrorMessage(response, ...))`, so the Error's message is already the server's reason.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
